/**
 * 인증 API 서비스
 * 로그인, 로그아웃, 사용자 정보 조회 등 인증 관련 API 요청을 처리합니다.
 */

import { api } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  User,
  LoginCredentials,
  LoginCodeResponse,
  SignupCredentials,
  ResetPasswordSendEmailCredentials,
  ResetPasswordSendEmailResponse,
  ResetPasswordResponse,
  ResetPasswordCredentials,
  SubscriptionInfo
} from '@/types/auth';

/**
 * 사용자 인증 정보 조회
 * @param token JWT 토큰
 * @returns Promise<ApiResponse<User>> 사용자 정보 응답
 */
export async function fetchUserInfo(token: string): Promise<ApiResponse<User>> {
  return api.get<User>('/user/me', {}, token);
}

/**
 * 로그인 요청
 * @param credentials 로그인 정보 (이메일, 비밀번호)
 * @returns Promise<ApiResponse<{ token: string; user: User }>> 로그인 응답
 */
export async function signin(credentials: LoginCredentials, platform?: string): Promise<ApiResponse<LoginCodeResponse>> {
  return api.post<LoginCodeResponse>(`/auth/signin/${platform}`, { body: credentials });
}

/**
 * 회원가입 요청
 * @param credentials 회원가입 정보 (이메일, 비밀번호, userId)
 * @param platform 플랫폼
 * @returns Promise<ApiResponse<LoginCodeResponse>> 회원가입 응답
 */
export async function signup(credentials: SignupCredentials, platform?: string): Promise<ApiResponse<LoginCodeResponse>> {
  return api.post<LoginCodeResponse>(`/auth/signup/${platform}`, { body: credentials });
}

/**
 * 비밀번호 재설정 이메일 요청
 * @param credentials 비밀번호 재설정 이메일 정보 (이메일)
 * @param platform 플랫폼
 * @returns Promise<ApiResponse<ResetPasswordSendEmailResponse>> 비밀번호 재설정 이메일 응답
 */
export async function resetPwSendEmail(credentials: ResetPasswordSendEmailCredentials, platform?: string): Promise<ApiResponse<ResetPasswordSendEmailResponse>> {
  return api.post<ResetPasswordSendEmailResponse>(`/auth/resetpw/sendemail/${platform}`, { body: credentials });
}

/**
 * 비밀번호 재설정 요청
 * @param credentials 비밀번호 재설정 정보 (이메일)
 * @param platform 플랫폼
 * @returns Promise<ApiResponse<ResetPasswordResponse>> 비밀번호 재설정 응답
 */
export async function resetPw(credentials: ResetPasswordCredentials, platform?: string): Promise<ApiResponse<ResetPasswordResponse>> {
  return api.post<ResetPasswordResponse>(`/auth/resetpw/${platform}`, { body: credentials });
}

/**
 * 구독 정보 조회
 * @returns Promise<ApiResponse<User>> 사용자 정보 응답
 */
export async function fetchSubscriptionInfo(): Promise<ApiResponse<SubscriptionInfo>> {
  return api.get<SubscriptionInfo>('/user/me/subscription');
}

/**
 * 구독 취소
 * @returns Promise<ApiResponse<boolean>> 구독 취소 응답
 */
export async function cancelSubscription(): Promise<ApiResponse<boolean>> {
  return api.post<boolean>('/user/me/subscription/cancel');
}

/**
 * 구독 취소 롤백
 * @returns Promise<ApiResponse<boolean>> 구독 취소 롤백 응답
 */
export async function cancelSubscriptionRollback(): Promise<ApiResponse<boolean>> {
  return api.post<boolean>('/user/me/subscription/cancel/rollback');
}
